import PropTypes from 'prop-types';

// material-ui
import {
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Box,
  Divider,
  Paper
  // TableFooter,
} from '@mui/material';

// third-party
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  getSortedRowModel,
  getExpandedRowModel
} from '@tanstack/react-table';
import { alpha, useTheme } from '@mui/material/styles';

// project-import
import ScrollX from '@/from-theme/components/ScrollX';
import MainCard from '@/from-theme/components/MainCard';
import {
  CSVExport,
  TablePagination,
  HeaderSort,
  SelectColumnVisibility
  // SelectColumnSorting,
} from '@/from-theme/components/third-party/react-table';
import { Fragment, useState } from 'react';
import PDFExport from '@/from-theme/components/third-party/react-table/PDFExport/PDFExport';
import { useIntl } from 'react-intl';
import { EmptyTable } from '@/from-theme/components/third-party/react-table';
import { apiStatuses } from '@/store/api/constants';
import TableLoader from './components/table-loader/table-loader';
import TableError from './components/table-error/table-error';
import './CustomTable.scss';

// ==============================|| REACT TABLE ||============================== //

export default function TableView({
  data,
  columns: columnsLocale,
  top,
  title,
  actionButton,
  printButton,
  reportFileName = '',
  apiStatus,
  apiErrorMsg,
  renderRowTable,
  isTableDetails,
  rowSelection = {},
  setRowSelection,
  initialPageSize
}) {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const backColor = alpha(theme.palette.primary.lighter, 0.1);

  const columns = [];
  const hiddenColumns = {};
  columnsLocale.forEach((item) => {
    // generate actual columns
    columns.push({
      ...item,
      header: typeof item.header === 'string' ? formatMessage({ id: item.header }) : item.header
    });

    //generate hidden columns
    if (item?.hideColumn && !!item.hideColumn) {
      hiddenColumns[item?.accessorKey] = false;
    }
  });

  // const matchDownSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [sorting, setSorting] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState(hiddenColumns);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection
    },
    autoResetPageIndex: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,

    getRowCanExpand: () => true,
    getExpandedRowModel: getExpandedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    ...(setRowSelection
      ? {
          enableRowSelection: true,
          onRowSelectionChange: setRowSelection
        }
      : {})
  });

  let headers = [];
  table.getAllColumns().map((columns) =>
    headers.push({
      label: typeof columns.columnDef.header === 'string' ? columns.columnDef.header : '#',
      // @ts-ignore
      key: columns.columnDef.accessorKey
    })
  );

  let selectedHeaders = [];
  table.getVisibleLeafColumns().map((columns) =>
  selectedHeaders.push({
      label: typeof columns.columnDef.header === 'string' ? columns.columnDef.header : '#',
      // @ts-ignore
      key: columns.columnDef.accessorKey
    })
  );

  return (
    <MainCard
      title={title || 'hidden'}
      content={false}
      className={!title ? 'hide-main-card-title' : ''}
      secondary={
        <>
          <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }}>
            {!!printButton && (
              <>
                {' '}
                <SelectColumnVisibility
                  {...{
                    getVisibleLeafColumns: table.getVisibleLeafColumns,
                    getIsAllColumnsVisible: table.getIsAllColumnsVisible,
                    getToggleAllColumnsVisibilityHandler:
                     table.getToggleAllColumnsVisibilityHandler,
                    getAllColumns: table.getAllColumns
                  }}
                />
                {/* <SelectColumnSorting {...{
              getState: table.getState, getAllColumns: table.getAllColumns, setSorting
            }} /> */}
                <CSVExport
                  {...{
                    data: table.getSortedRowModel().rows.map((d) => d.original),
                    headers: selectedHeaders,
                    filename: reportFileName
                  }}
                />
                <PDFExport
                  {...{
                    data: table.getSortedRowModel().rows.map((d) => d.original),
                    headers: selectedHeaders,
                    filename: reportFileName
                  }}
                />
              </>
            )}
            {!!actionButton && actionButton}
          </Stack>
        </>
      }
    >
      <ScrollX>
        <Stack>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      if (header.column.columnDef.meta !== undefined && 
                        header.column.getCanSort()) {
                        Object.assign(header.column.columnDef.meta, {
                          className: header.column.columnDef.meta.className + 
                          ' cursor-pointer prevent-select'
                        });
                      }

                      return (
                        <TableCell
                          key={header.id}
                          {...header.column.columnDef.meta}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          sx={{
                              // Optional: Add specific styling for the grouped headers
                              textAlign: 'center', 
                             // border: '1px solid rgba(224, 224, 224, 1)',
                              fontWeight: header.column.parent ? 400 : 700 // Bold parent headers
                            }}
                          {...(header.column.getCanSort() &&
                            header.column.columnDef.meta === undefined && {
                              className: 'cursor-pointer prevent-select'
                            })}
                        >
                          {header.isPlaceholder ? null : (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box>{flexRender(header.column.columnDef.header,
                                 header.getContext())}</Box>
                              {header.column.getCanSort() && <HeaderSort column={header.column} />}
                            </Stack>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                <>
                  {(apiStatus === apiStatuses.succeeded || apiStatus === undefined) &&
                    (table.getRowModel().rows.length > 0 ? (
                      table.getRowModel().rows.map((row) => (
                        <Fragment key={`table-view${row.id}`}>
                          <TableRow>
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id} {...cell.column.columnDef.meta}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                          {row.getIsExpanded() &&
                            Boolean(renderRowTable) &&
                            (!isTableDetails ? (
                              renderRowTable(row)
                            ) : (
                              <TableRow
                                sx={{
                                  bgcolor: backColor,
                                  '&:hover': { bgcolor: `${backColor} !important` }
                                }}
                              >
                                <TableCell colSpan={row.getVisibleCells().length}>
                                  {renderRowTable(row)}</TableCell>
                              </TableRow>
                            ))}
                        </Fragment>
                      ))
                    ) : (
                      <Fragment>
                        <TableRow>
                          <TableCell colSpan={table.getAllColumns().length}>
                            <EmptyTable msg={formatMessage({ id: 'no-data' })} />
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    ))}

                  {apiStatus === apiStatuses.pending && <TableLoader 
                  colSpan={table.getAllColumns().length} />}

                  {apiStatus === apiStatuses.rejected && <TableError 
                  colSpan={table.getAllColumns().length} message={apiErrorMsg} />}
                </>
              </TableBody>
              {/* <TableFooter>
                {table.getFooterGroups().map((footerGroup) => (
                  <TableRow key={footerGroup.id}>
                    {footerGroup.headers.map((footer) => (
                      <TableCell key={footer.id} {...footer.column.columnDef.meta}>
                        {footer.isPlaceholder
                          ? null : flexRender(footer.column.columnDef.header, footer.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableFooter> */}
            </Table>
          </TableContainer>

          {!top && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <TablePagination
                initialPageSize={initialPageSize}
                  {...{
                    setPageSize: table.setPageSize,
                    setPageIndex: table.setPageIndex,
                    getState: table.getState,
                    getPageCount: table.getPageCount
                  }}
                />
              </Box>
            </>
          )}
        </Stack>
      </ScrollX>
    </MainCard>
  );
}

TableView.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  top: PropTypes.bool,
  title: PropTypes.string,
  actionButton: PropTypes.bool,
  printButton: PropTypes.bool,
  showDropdown: PropTypes.bool,
  reportFileName: PropTypes.string,
  apiStatus: PropTypes.string,
  apiErrorMsg: PropTypes.string,
  renderRowTable: PropTypes.func,
  isTableDetails: PropTypes.bool,
  rowSelection: PropTypes.object,
  setRowSelection: PropTypes.func,
  initialPageSize: PropTypes.number
};
